/**
 * Modifier Resolution Service
 *
 * Resolves team-specific terminology to canonical modifiers,
 * validates modifier combinations for conflicts, and computes
 * the final FormationState by applying modifiers.
 */

import type {
	Modifier,
	ModifierApplicationResult,
	ModifierConflict,
	ModifierTerminology,
	ValidatedModifierSet,
} from '../types/Modifier.types'
import type { FormationState } from '../types/FormationState.types'
import { cloneFormationState } from '../types/FormationState.types'

/**
 * Resolves team terminology to canonical modifier name
 *
 * @param teamTerm - Team-specific modifier name (e.g., "Sexy", "Change")
 * @param terminology - Playbook terminology mappings
 * @returns Canonical modifier name or original term if no mapping exists
 */
export function resolveTerminology(
	teamTerm: string,
	terminology: Map<string, ModifierTerminology>,
): string {
	// Check if there's a mapping for this team term
	const mapping = Array.from(terminology.values()).find(
		t => t.team_term.toLowerCase() === teamTerm.toLowerCase(),
	)

	return mapping ? mapping.canonical_modifier : teamTerm
}

/**
 * Resolves multiple team terms to canonical modifiers
 *
 * @param teamTerms - Array of team-specific modifier names
 * @param terminology - Playbook terminology mappings
 * @returns Array of canonical modifier names
 */
export function resolveTerminologyBatch(
	teamTerms: string[],
	terminology: Map<string, ModifierTerminology>,
): string[] {
	return teamTerms.map(term => resolveTerminology(term, terminology))
}

/**
 * Validates a set of modifiers for conflicts
 *
 * @param modifierNames - Array of canonical modifier names to validate
 * @param conflicts - Available conflict definitions
 * @returns Validation result with any conflicts found
 */
export function validateModifiers(
	modifierNames: string[],
	conflicts: ModifierConflict[],
): ValidatedModifierSet {
	const foundConflicts: Array<{
		modifier_a: string
		modifier_b: string
		reason: string
	}> = []

	// Check each pair of modifiers
	for (let i = 0; i < modifierNames.length; i++) {
		for (let j = i + 1; j < modifierNames.length; j++) {
			const modA = modifierNames[i]
			const modB = modifierNames[j]
			if (!modA || !modB) continue

			// Check if this pair conflicts
			const conflict = conflicts.find(
				c =>
					(c.modifier_a === modA && c.modifier_b === modB) ||
					(c.modifier_a === modB && c.modifier_b === modA),
			)

			if (conflict) {
				foundConflicts.push({
					modifier_a: modA,
					modifier_b: modB,
					reason: conflict.conflict_reason,
				})
			}
		}
	}

	return {
		modifiers: modifierNames,
		conflicts: foundConflicts,
		isValid: foundConflicts.length === 0,
	}
}

/**
 * Computes final FormationState by applying modifiers
 *
 * @param baseState - Starting formation state (from base formation)
 * @param modifierNames - Array of canonical modifier names to apply
 * @param modifiers - Available modifier definitions
 * @param conflicts - Available conflict definitions
 * @returns Application result with final state and any warnings/errors
 */
export function computeState(
	baseState: FormationState,
	modifierNames: string[],
	modifiers: Map<string, Modifier>,
	conflicts: ModifierConflict[],
): ModifierApplicationResult {
	const warnings: string[] = []
	const errors: string[] = []
	const appliedModifiers: string[] = []

	// Validate modifiers first
	const validation = validateModifiers(modifierNames, conflicts)
	if (!validation.isValid) {
		validation.conflicts.forEach(conflict => {
			errors.push(
				`Conflict between "${conflict.modifier_a}" and "${conflict.modifier_b}": ${conflict.reason}`,
			)
		})

		// Return base state unchanged if there are conflicts
		return {
			state: baseState,
			appliedModifiers: [],
			warnings,
			errors,
		}
	}

	// Clone base state to avoid mutations
	let currentState = cloneFormationState(baseState)

	// Apply each modifier in order
	for (const modifierName of modifierNames) {
		const modifier = modifiers.get(modifierName)

		if (!modifier) {
			warnings.push(`Unknown modifier: ${modifierName}`)
			continue
		}

		// Apply state changes
		currentState = {
			...currentState,
			...modifier.state_changes,
			// Preserve custom_flags, merging any from the modifier
			custom_flags: {
				...currentState.custom_flags,
				...(modifier.state_changes.custom_flags || {}),
			},
		}

		appliedModifiers.push(modifierName)
	}

	return {
		state: currentState,
		appliedModifiers,
		warnings,
		errors,
	}
}

/**
 * Gets a modifier definition by canonical name
 *
 * @param canonicalName - Canonical modifier identifier
 * @param modifiers - Available modifier definitions
 * @returns Modifier definition or null if not found
 */
export function getModifier(
	canonicalName: string,
	modifiers: Map<string, Modifier>,
): Modifier | null {
	return modifiers.get(canonicalName) ?? null
}

/**
 * Checks if a single modifier conflicts with any in a set
 *
 * @param modifier - Modifier to check
 * @param existingModifiers - Set of already-applied modifiers
 * @param conflicts - Available conflict definitions
 * @returns Array of conflicts found, empty if none
 */
export function checkModifierConflict(
	modifier: string,
	existingModifiers: string[],
	conflicts: ModifierConflict[],
): Array<{ conflictsWith: string; reason: string }> {
	const foundConflicts: Array<{ conflictsWith: string; reason: string }> = []

	for (const existing of existingModifiers) {
		const conflict = conflicts.find(
			c =>
				(c.modifier_a === modifier && c.modifier_b === existing) ||
				(c.modifier_a === existing && c.modifier_b === modifier),
		)

		if (conflict) {
			foundConflicts.push({
				conflictsWith: existing,
				reason: conflict.conflict_reason,
			})
		}
	}

	return foundConflicts
}

/**
 * Suggests compatible modifiers that can be added to a set
 *
 * @param existingModifiers - Currently applied modifiers
 * @param allModifiers - All available modifiers
 * @param conflicts - Available conflict definitions
 * @returns Array of canonical modifier names that can be safely added
 */
export function getCompatibleModifiers(
	existingModifiers: string[],
	allModifiers: Map<string, Modifier>,
	conflicts: ModifierConflict[],
): string[] {
	const compatible: string[] = []

	for (const [canonicalName] of allModifiers) {
		// Skip if already applied
		if (existingModifiers.includes(canonicalName)) {
			continue
		}

		// Check for conflicts
		const hasConflict = checkModifierConflict(
			canonicalName,
			existingModifiers,
			conflicts,
		)

		if (hasConflict.length === 0) {
			compatible.push(canonicalName)
		}
	}

	return compatible
}

/**
 * Modifier Resolution Service
 * Provides all modifier resolution functionality as a service object
 */
export const ModifierResolutionService = {
	resolveTerminology,
	resolveTerminologyBatch,
	validateModifiers,
	computeState,
	getModifier,
	checkModifierConflict,
	getCompatibleModifiers,
}
export const modifierResolutionService = ModifierResolutionService // Backward compatibility alias
